import { useCallback, useEffect, useRef } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useNodesInitialized,
  useReactFlow,
  useStore,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
  type XYPosition,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { GitMerge, Flag, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DAY_MS, formatLagExact } from '@/lib/lag'
import { versionLabel } from '@/lib/versioning'
import {
  computeFlowLayers,
  wouldCreateCycle,
  type FlowStep,
} from '@/lib/procedureFlowGraph'
import type { FormCategory, FormDefinition } from '@/types/cgt'
import { FORM_CATEGORY_COLORS } from '@/types/cgt'
import { allFields } from '@/lib/formSections'

export interface BuilderStep extends FlowStep {
  formDefinitionId: string
  /**
   * How many times this step is performed; null = does not repeat
   * (z8nrz7dkkc). Optional so a caller that does not author repeats (the
   * read-only preview dialog) need not invent one.
   */
  repeatCount?: number | null
}

/** Per-edge lag config keyed by `${predKey}->${stepKey}`. */
export type EdgeLagMap = Record<string, { minLagDays?: number; maxLagDays?: number }>

interface ProcedureFlowBuilderProps {
  steps: BuilderStep[]
  definitions?: FormDefinition[]
  selectedKey?: string | null
  onSelectStep?: (key: string | null) => void
  /** Add `predKey` as a predecessor of `stepKey` (one new dependency edge). */
  onAddDependency?: (stepKey: string, predKey: string) => void
  /** Remove the `predKey → stepKey` dependency. */
  onRemoveDependency?: (stepKey: string, predKey: string) => void
  onRemoveStep?: (key: string) => void
  onEditForm?: (formDefinitionId: string) => void
  /** Lag (min/max days) per edge, shown as the arrow label. */
  edgeLag?: EdgeLagMap
  /** `${predKey}->${stepKey}` of the currently selected arrow. */
  selectedEdgeId?: string | null
  /** Fired when an arrow is clicked (null when the canvas is clicked). */
  onSelectEdge?: (edge: { predKey: string; stepKey: string } | null) => void
  className?: string
  /** Static viewer mode: no dragging, connecting, or delete/edit affordances. */
  readOnly?: boolean
}

type FormNodeData = {
  label: string
  /**
   * The version this step is PINNED to (z8nrz7cnhh).
   *
   * A step stores a `formDefinitionId`, which names one version, not a family —
   * so two versions of the same form are two different steps that render with
   * the same name. Without this the canvas could not tell them apart, and
   * neither could the person reading it.
   *
   * Formatted through the shared `versionLabel` (V3), and preferring a custom
   * `versionName` where the author set one — the same precedence
   * FormDefinitionsPage uses, so one form does not read "V3" here and by its
   * given name there.
   *
   * Null only when the definition is not in the loaded list (a form outside this
   * tenant's scope, or one still loading); the badge is then omitted rather than
   * guessed at, since "V1" is `versionLabel`'s default for an absent version and
   * would state a version we do not actually know.
   */
  versionLabel?: string | null
  /**
   * How many times this step is performed (z8nrz7dkkc). Null, or 1, renders no
   * badge — a canvas full of "×1" would say nothing and cost the space the
   * version badge needs.
   */
  repeatCount?: number | null
  index: number
  isStart: boolean
  isMerge: boolean
  predCount: number
  selected: boolean
  category?: FormCategory
  hasAnchoredFields: boolean
  formDefinitionId: string
  readOnly?: boolean
  onEdit?: (formDefinitionId: string) => void
  onDelete?: (key: string) => void
}

const NODE_W = 200
const COL_GAP = 60
const ROW_GAP = 110

/**
 * Shared by the `fitView` prop and the explicit fit below, so the two can never
 * disagree about the zoom ceiling.
 *
 * `maxZoom: 1` is deliberate: fitting is for bringing a large flow into view,
 * never for magnifying a small one. A three-step template renders at 100% in
 * the middle of an empty canvas rather than blown up to fill it (z8nrz7dewq
 * AC 2.2). `padding` keeps the outermost nodes off the edges so nothing is
 * clipped by the fit itself.
 */
const FIT_VIEW_OPTIONS = { padding: 0.25, maxZoom: 1 } as const

/** Layered top-down positions: roots on top, parallel siblings side-by-side. */
function layeredPositions(steps: BuilderStep[]): Map<string, XYPosition> {
  const layers = computeFlowLayers(steps)
  const positions = new Map<string, XYPosition>()
  const maxRow = Math.max(1, ...layers.map(l => l.nodes.length))
  const totalWidth = maxRow * NODE_W + (maxRow - 1) * COL_GAP

  layers.forEach(layer => {
    const rowWidth = layer.nodes.length * NODE_W + (layer.nodes.length - 1) * COL_GAP
    const startX = (totalWidth - rowWidth) / 2
    layer.nodes.forEach((node, i) => {
      positions.set(node.key, {
        x: startX + i * (NODE_W + COL_GAP),
        y: layer.depth * ROW_GAP,
      })
    })
  })
  return positions
}

function FormNode({ id, data }: NodeProps<Node<FormNodeData>>) {
  const anchoredHex = data.category === 'anchored' ? FORM_CATEGORY_COLORS.anchored : undefined
  return (
    <div
      className={cn(
        'group relative w-[200px] rounded-xl border bg-card px-3 py-2.5 shadow-sm transition-all',
        'hover:shadow-md',
        data.selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200',
        data.isMerge && !data.selected && 'border-amber-300 bg-amber-50/60',
      )}
      style={anchoredHex && !data.selected ? { borderColor: `${anchoredHex}4d`, backgroundColor: `${anchoredHex}0d` } : undefined}
    >
      <Handle
        type='target'
        position={Position.Top}
        className='!h-2.5 !w-2.5 !border-2 !border-white !bg-slate-400'
      />

      <div className='flex items-start gap-2'>
        <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white'>
          {data.index}
        </span>
        <span className='line-clamp-2 flex-1 text-xs font-semibold leading-snug'>
          {data.hasAnchoredFields && (
            <span
              className='mr-1 inline-block h-2 w-2 rounded-full bg-red-500 align-middle'
              title='Contains anchored fields'
            />
          )}
          {data.label}
        </span>
        {/* That this step repeats, and how many times (z8nrz7dkkc). Sits with
            the version badge because both answer "which thing is this step,
            exactly" — and like it, is omitted entirely at 1 rather than shown
            as a redundant "×1". */}
        {data.repeatCount != null && data.repeatCount > 1 && (
          <span
            className='mt-0.5 shrink-0 rounded bg-slate-200 px-1 py-px text-[10px] font-semibold tabular-nums text-slate-700'
            title={`Performed ${data.repeatCount} times`}
          >
            ×{data.repeatCount}
          </span>
        )}
        {data.versionLabel && (
          <span
            className='mt-0.5 shrink-0 rounded bg-slate-100 px-1 py-px text-[10px] font-semibold tabular-nums text-slate-600'
            title={`Pinned to ${data.versionLabel}`}
          >
            {data.versionLabel}
          </span>
        )}
      </div>

      <div className='mt-1.5 flex items-center gap-1.5 pl-7'>
        {data.isStart && (
          <span className='inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700'>
            <Flag className='h-2.5 w-2.5' />
            start
          </span>
        )}
        {data.isMerge && (
          <span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800'>
            <GitMerge className='h-2.5 w-2.5' />
            waits {data.predCount}
          </span>
        )}
      </div>

      {/* hover actions */}
      {!data.readOnly && (
        <div className='absolute -top-2.5 -right-2.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100'>
          {data.onEdit && (
            <button
              type='button'
              title='Edit this form'
              onClick={e => {
                e.stopPropagation()
                data.onEdit?.(data.formDefinitionId)
              }}
              className='flex h-6 w-6 items-center justify-center rounded-full border bg-white text-slate-500 shadow-sm hover:text-blue-600'
            >
              <Pencil className='h-3 w-3' />
            </button>
          )}
          <button
            type='button'
            title='Remove from flow'
            onClick={e => {
              e.stopPropagation()
              data.onDelete?.(id)
            }}
            className='flex h-6 w-6 items-center justify-center rounded-full border bg-white text-slate-500 shadow-sm hover:text-destructive'
          >
            <Trash2 className='h-3 w-3' />
          </button>
        </div>
      )}

      <Handle
        type='source'
        position={Position.Bottom}
        className='!h-2.5 !w-2.5 !border-2 !border-white !bg-blue-500'
      />
    </div>
  )
}

const nodeTypes = { form: FormNode }

function FlowCanvas({
  steps,
  definitions,
  selectedKey,
  onSelectStep,
  onAddDependency,
  onRemoveDependency,
  onRemoveStep,
  onEditForm,
  edgeLag,
  selectedEdgeId,
  onSelectEdge,
  readOnly,
}: ProcedureFlowBuilderProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<FormNodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const positionsRef = useRef<Map<string, XYPosition>>(new Map())

  // Rebuild nodes from `steps`, preserving any user-dragged positions.
  useEffect(() => {
    const layout = layeredPositions(steps)
    setNodes(prev => {
      const prevPos = new Map(prev.map(n => [n.id, n.position]))
      return steps.map((step, i) => {
        const position =
          prevPos.get(step.key) ??
          positionsRef.current.get(step.key) ??
          layout.get(step.key) ?? { x: 0, y: 0 }
        positionsRef.current.set(step.key, position)
        const def = definitions?.find(d => d.id === step.formDefinitionId)
        const hasAnchoredFields = def && def.category !== 'anchored'
          ? allFields(def).some(
              f =>
                f.anchored ||
                (f.tableConfig?.cells != null && Object.values(f.tableConfig.cells).some(c => c.anchored)),
            )
          : false
        return {
          id: step.key,
          type: 'form',
          position,
          data: {
            label: step.formName,
            repeatCount: step.repeatCount ?? null,
            versionLabel: def ? (def.versionName ?? versionLabel(def.version)) : null,
            index: i + 1,
            isStart: step.predecessorKeys.size === 0,
            isMerge: step.predecessorKeys.size > 1,
            predCount: step.predecessorKeys.size,
            selected: selectedKey === step.key,
            category: def?.category,
            hasAnchoredFields,
            formDefinitionId: step.formDefinitionId,
            readOnly,
            onEdit: onEditForm,
            onDelete: onRemoveStep,
          },
        } satisfies Node<FormNodeData>
      })
    })
  }, [steps, definitions, selectedKey, onRemoveStep, onEditForm, readOnly, setNodes])

  // Rebuild edges from predecessor relationships (with lag labels + selection).
  useEffect(() => {
    const next: Edge[] = []
    for (const step of steps) {
      for (const predKey of step.predecessorKeys) {
        const id = `${predKey}->${step.key}`
        const lag = edgeLag?.[id]
        const labelParts: string[] = []
        if (lag?.minLagDays != null) labelParts.push(`≥${formatLagExact(lag.minLagDays * DAY_MS)}`)
        if (lag?.maxLagDays != null) labelParts.push(`≤${formatLagExact(lag.maxLagDays * DAY_MS)}`)
        const isSelected = selectedEdgeId === id
        next.push({
          id,
          source: predKey,
          target: step.key,
          type: 'smoothstep',
          animated: false,
          label: labelParts.join('  '),
          labelStyle: { fontSize: 10, fill: '#1d4ed8', fontWeight: 600 },
          labelBgPadding: [4, 2],
          labelBgBorderRadius: 4,
          labelBgStyle: { fill: '#eff6ff' },
          markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
          style: {
            stroke: isSelected ? '#2563eb' : '#93c5fd',
            strokeWidth: isSelected ? 3 : 2,
          },
        })
      }
    }
    setEdges(next)
  }, [steps, edgeLag, selectedEdgeId, setEdges])

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!onAddDependency) return
      const { source, target } = conn
      if (!source || !target || source === target) return
      const step = steps.find(s => s.key === target)
      if (step?.predecessorKeys.has(source)) return
      if (wouldCreateCycle(steps, target, source)) {
        window.alert('That connection would create a circular dependency.')
        return
      }
      onAddDependency(target, source)
    },
    [steps, onAddDependency],
  )

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      deleted.forEach(e => onRemoveDependency?.(e.target, e.source))
    },
    [onRemoveDependency],
  )

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      deleted.forEach(n => onRemoveStep?.(n.id))
    },
    [onRemoveStep],
  )

  const onNodeDragStop = useCallback((_: unknown, node: Node) => {
    positionsRef.current.set(node.id, node.position)
  }, [])

  // ── Auto-fit on load ──────────────────────────────────────────────────────
  // The `fitView` prop alone is not enough here. Nodes arrive from an effect,
  // so the array is still empty on the pass React Flow would fit on, and the
  // canvas is sized from a measurement that lands after that again — both leave
  // the initial fit computed against a canvas or a graph that isn't the real
  // one. Fitting explicitly once the nodes report measured dimensions, and
  // again whenever the canvas itself changes size, is what actually gets every
  // node on screen (AC 2.1).
  const { fitView } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const canvasWidth = useStore(s => s.width)
  const canvasHeight = useStore(s => s.height)
  // Set on the first pan/zoom the user performs. After that the viewport is
  // theirs: a later resize must not yank the flow back to a fitted view and
  // throw away where they had navigated to.
  const userMovedViewport = useRef(false)

  useEffect(() => {
    if (!nodesInitialized || userMovedViewport.current) return
    if (canvasWidth === 0 || canvasHeight === 0) return
    void fitView(FIT_VIEW_OPTIONS)
  }, [nodesInitialized, canvasWidth, canvasHeight, fitView])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={readOnly ? undefined : onConnect}
      onEdgesDelete={readOnly ? undefined : onEdgesDelete}
      onNodesDelete={readOnly ? undefined : onNodesDelete}
      onNodeDragStop={readOnly ? undefined : onNodeDragStop}
      onNodeClick={(_, node) => {
        onSelectStep?.(node.id)
        onSelectEdge?.(null)
      }}
      onEdgeClick={(_, edge) => {
        onSelectEdge?.({ predKey: edge.source, stepKey: edge.target })
        onSelectStep?.(null)
      }}
      onPaneClick={() => {
        onSelectStep?.(null)
        onSelectEdge?.(null)
      }}
      onMoveStart={(event) => {
        // Programmatic moves (our own fitView) come through with a null event;
        // only a real gesture hands the viewport over to the user.
        if (event) userMovedViewport.current = true
      }}
      nodesDraggable={!readOnly}
      nodesConnectable={!readOnly}
      elementsSelectable={!readOnly}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      defaultEdgeOptions={{ type: 'smoothstep' }}
      proOptions={{ hideAttribution: true }}
      className='bg-slate-50'
    >
      <Background gap={18} color='#e2e8f0' />
      {/* The fit-to-view control shares the load-time options so pressing it
          reproduces the view the page opened with. Without this it uses React
          Flow's own default ceiling of 2x, which on the enlarged canvas meant a
          small flow jumped to 200% — a jarring result for a button the user
          reaches for precisely to get back to the fitted view. */}
      <Controls showInteractive={false} fitViewOptions={FIT_VIEW_OPTIONS} />
      <MiniMap
        pannable
        zoomable
        nodeColor={n =>
          (n.data as FormNodeData)?.isMerge ? '#fcd34d' : '#bfdbfe'
        }
        className='!bg-white'
      />
    </ReactFlow>
  )
}

/**
 * Visual DAG editor for a procedure. Nodes are forms, edges are dependencies.
 * Drag from a node's bottom handle to another node's top handle to make the
 * second form wait for the first. Select an edge and press Backspace to remove
 * the dependency; select a node and press Backspace to remove the form.
 */
export function ProcedureFlowBuilder(props: ProcedureFlowBuilderProps) {
  return (
    <div className={cn('h-[520px] w-full overflow-hidden rounded-xl border', props.className)}>
      <ReactFlowProvider>
        <FlowCanvas {...props} />
      </ReactFlowProvider>
    </div>
  )
}
